import { hsvToRgbNormalized, rgbaToCss } from '../utils/color.js';
import { isDisplayP3Supported, copyCanvasSrgbToP3 } from '../utils/p3.js';

/**
 * Render the HSV color area gradient onto a canvas.
 *
 * ```
 *  white ┌────────┐ saturated color
 *        │        │
 *   gray │   ◉    │ dark color
 *        │        │
 *  black └────────┘ black
 * ```
 *
 * Strategy:
 * 1. Fill with the fully saturated hue color
 * 2. Overlay horizontal gradient: white → transparent (left to right)
 * 3. Overlay vertical gradient: transparent → black (top to bottom)
 *
 * When `colorSpace` is `'display-p3'` and the browser doesn't support CSS Color Level 4
 * `color()` syntax in canvas `fillStyle`, rendering falls back to the sRGB→P3 polyfill:
 * gradients are drawn to an offscreen sRGB canvas, then pixel data is relabeled as P3.
 *
 * @see {@link https://caniuse.com/mdn-api_htmlcanvaselement_getcontext_2d_context_options_colorspace_parameter | Can I Use: canvas colorSpace parameter}
 */
function renderColorAreaHSB(canvas, hue, colorSpace) {
    renderToCanvas(canvas, hue, colorSpace, renderHSBGradients);
}
/**
 * Render the HSL color area gradient onto a canvas.
 *
 * ```
 *  white ┌────────┐ white
 *        │        │
 *   gray │   ◉    │ saturated color
 *        │        │
 *  black └────────┘ black
 * ```
 *
 * Strategy:
 * 1. Fill with the fully saturated hue color
 * 2. Overlay horizontal gradient: gray → transparent (left to right)
 * 3. Overlay vertical gradient: white → transparent → black (top to bottom, split at midpoint)
 *
 * @see {@link renderColorAreaHSB} for P3 polyfill details
 */
function renderColorAreaHSL(canvas, hue, colorSpace) {
    renderToCanvas(canvas, hue, colorSpace, renderHSLGradients);
}
function renderHSBGradients(ctx, hue, width, height, colorSpace) {
    const { r, g, b } = hsvToRgbNormalized(hue, 1, 1);
    // Fill with the fully saturated hue
    ctx.fillStyle = rgbaToCss(r, g, b, 1, colorSpace);
    ctx.fillRect(0, 0, width, height);
    // Horizontal gradient: white → transparent (left to right)
    const horizontal = ctx.createLinearGradient(0, 0, width, 0);
    horizontal.addColorStop(0, rgbaToCss(1, 1, 1, 1, colorSpace));
    horizontal.addColorStop(1, rgbaToCss(1, 1, 1, 0, colorSpace));
    ctx.fillStyle = horizontal;
    ctx.fillRect(0, 0, width, height);
    // Vertical gradient: transparent → black (top to bottom)
    const vertical = ctx.createLinearGradient(0, 0, 0, height);
    vertical.addColorStop(0, rgbaToCss(0, 0, 0, 0, colorSpace));
    vertical.addColorStop(1, rgbaToCss(0, 0, 0, 1, colorSpace));
    ctx.fillStyle = vertical;
    ctx.fillRect(0, 0, width, height);
}
function renderToCanvas(canvas, hue, colorSpace, renderGradients) {
    const { width, height } = canvas;
    if (width === 0 || height === 0)
        return;
    const ctx = canvas.getContext('2d', { colorSpace });
    if (!ctx)
        return;
    if (colorSpace === 'display-p3' && !isDisplayP3Supported()) {
        // Render gradients to an offscreen sRGB canvas
        const offscreen = document.createElement('canvas');
        offscreen.width = width;
        offscreen.height = height;
        const srgbCtx = offscreen.getContext('2d', { colorSpace: 'srgb' });
        if (!srgbCtx)
            return;
        renderGradients(srgbCtx, hue, width, height, 'srgb');
        // Copy sRGB pixel data to the P3 canvas by relabeling
        copyCanvasSrgbToP3(srgbCtx, ctx);
    }
    else {
        renderGradients(ctx, hue, width, height, colorSpace);
    }
}
function renderHSLGradients(ctx, hue, width, height, colorSpace) {
    const { r, g, b } = hsvToRgbNormalized(hue, 1, 1);
    // Fill with the fully saturated hue
    ctx.fillStyle = rgbaToCss(r, g, b, 1, colorSpace);
    ctx.fillRect(0, 0, width, height);
    // Horizontal gradient: gray → transparent (left to right)
    const horizontal = ctx.createLinearGradient(0, 0, width, 0);
    horizontal.addColorStop(0, rgbaToCss(0.5, 0.5, 0.5, 1, colorSpace));
    horizontal.addColorStop(1, rgbaToCss(0.5, 0.5, 0.5, 0, colorSpace));
    ctx.fillStyle = horizontal;
    ctx.fillRect(0, 0, width, height);
    // Vertical gradient: white → transparent → black (top to bottom, split at midpoint)
    const vertical = ctx.createLinearGradient(0, 0, 0, height);
    vertical.addColorStop(0, rgbaToCss(1, 1, 1, 1, colorSpace));
    vertical.addColorStop(0.499999, rgbaToCss(1, 1, 1, 0, colorSpace));
    vertical.addColorStop(0.500001, rgbaToCss(0, 0, 0, 0, colorSpace));
    vertical.addColorStop(1, rgbaToCss(0, 0, 0, 1, colorSpace));
    ctx.fillStyle = vertical;
    ctx.fillRect(0, 0, width, height);
}

export { renderColorAreaHSB, renderColorAreaHSL };
//# sourceMappingURL=color-area.render.js.map
